// Premium dark glass UI primitives for the MTN Shop Social Hub.
import React from 'react';
import { cn } from '@/lib/utils';

export function Glass({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn(
      'rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl',
      'shadow-[0_8px_32px_rgba(0,0,0,0.45)]',
      className,
    )}>
      {children}
    </div>
  );
}

export function SectionTitle({ kicker, title, desc }: { kicker?: string; title: string; desc?: string }) {
  return (
    <div className="mb-6">
      {kicker && <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-400/90 mb-1">{kicker}</p>}
      <h2 className="text-2xl md:text-3xl font-semibold text-white tracking-tight">{title}</h2>
      {desc && <p className="mt-1.5 text-sm text-zinc-400 max-w-2xl">{desc}</p>}
    </div>
  );
}

export function GButton({
  children, onClick, variant = 'primary', disabled, className, type = 'button',
}: {
  children: React.ReactNode; onClick?: () => void;
  variant?: 'primary' | 'ghost' | 'danger' | 'success';
  disabled?: boolean; className?: string; type?: 'button' | 'submit';
}) {
  const styles = {
    primary: 'bg-white text-black hover:bg-zinc-200',
    ghost: 'bg-white/[0.06] text-white hover:bg-white/[0.12] border border-white/10',
    danger: 'bg-red-500/15 text-red-300 hover:bg-red-500/25 border border-red-500/20',
    success: 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border border-emerald-500/20',
  } as const;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-medium',
        'transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98]',
        styles[variant], className,
      )}
    >
      {children}
    </button>
  );
}

export function Pill({ children, tone = 'zinc' }: { children: React.ReactNode; tone?: 'zinc' | 'sky' | 'emerald' | 'amber' | 'red' | 'fuchsia' }) {
  const tones = {
    zinc: 'bg-white/[0.07] text-zinc-300 border-white/10',
    sky: 'bg-sky-500/10 text-sky-300 border-sky-500/20',
    emerald: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
    amber: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
    red: 'bg-red-500/10 text-red-300 border-red-500/20',
    fuchsia: 'bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/20',
  } as const;
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium', tones[tone])}>
      {children}
    </span>
  );
}

export function StatCard({ label, value, sub, icon }: { label: string; value: React.ReactNode; sub?: string; icon?: React.ReactNode }) {
  return (
    <Glass className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">{label}</p>
          <p className="mt-1 text-2xl font-semibold text-white tracking-tight">{value}</p>
          {sub && <p className="mt-0.5 text-xs text-zinc-500">{sub}</p>}
        </div>
        {icon && <div className="rounded-xl bg-white/[0.06] p-2.5 text-sky-300">{icon}</div>}
      </div>
    </Glass>
  );
}

export function ProgressBar({ value, tone = 'sky' }: { value: number; tone?: 'sky' | 'emerald' }) {
  return (
    <div className="h-1.5 w-full rounded-full bg-white/[0.07] overflow-hidden">
      <div
        className={cn('h-full rounded-full transition-all duration-700', tone === 'sky' ? 'bg-sky-400' : 'bg-emerald-400')}
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
}

export function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-zinc-400">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-zinc-600">{hint}</span>}
    </label>
  );
}

export const inputCls =
  'w-full rounded-xl border border-white/10 bg-white/[0.05] px-3.5 py-2.5 text-sm text-white ' +
  'placeholder:text-zinc-600 outline-none transition focus:border-sky-400/50 focus:bg-white/[0.07]';

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cn('h-4 w-4 animate-spin', className)} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}
